import { useEffect, useMemo, useState } from 'react';
import { cachedIcd11Names, fetchIcd11Names } from '../services/icd11Names';
import {
    diagnosisLabelsFromRecords, submissionDiagnosisValues, submissionRecords, SubmissionLike, SurveyLike, unnamedDiagnosisCodes,
} from '../utils/icd11Display';

/**
 * SKIN-612 — the WHO names of `codes`, from the API's catalogue (services/icd11Names). Starts
 * from the names already known and fills in the rest when the lookup answers; asks nothing
 * when there is no code or no study. Pass only codes no stored label names
 * (`unnamedDiagnosisCodes`): a name the visit stores always wins (`withCatalogueNames`).
 */
export function useIcd11CatalogueNames(uuidInvestigation: string | undefined, codes: ReadonlyArray<string>): ReadonlyMap<string, string> {
    // One key per set of codes, so a re-render with the same codes neither refetches nor resets.
    const key = Array.from(new Set(codes)).sort().join('\n');
    const [names, setNames] = useState<ReadonlyMap<string, string>>(() => cachedIcd11Names(codes));

    useEffect(() => {
        const wanted = key ? key.split('\n') : [];
        setNames(cachedIcd11Names(wanted));
        if (!uuidInvestigation || wanted.length === 0) return undefined;
        let live = true;
        fetchIcd11Names(uuidInvestigation, wanted).then(found => { if (live) setNames(found); });
        return () => { live = false; };
    }, [uuidInvestigation, key]);

    return names;
}

/**
 * The catalogue names a submission's ICD-11 values need: its codes that neither their own
 * label nor any pick of the submission names. For a PDF, which is rendered from props and
 * cannot fetch for itself.
 */
export function useSubmissionCatalogueNames(
    uuidInvestigation: string | undefined,
    submission: SubmissionLike | null | undefined,
    survey: SurveyLike | null | undefined,
): ReadonlyMap<string, string> {
    const codes = useMemo(
        () => unnamedDiagnosisCodes(submissionDiagnosisValues(submission, survey), diagnosisLabelsFromRecords(submissionRecords(submission))),
        [submission, survey],
    );
    return useIcd11CatalogueNames(uuidInvestigation, codes);
}
